import { useState, useEffect } from "react";
import { STATES_DATA, COMMODITIES } from "../data/constants";
import { getLangFont, getName, generatePrices, selectStyles, FILTER_LABEL_STYLE } from "../utils/helpers";
import { TrendBadge } from "../components/SharedUI";

/**
 * RatesPage
 * Lets the user pick State → Division/District → Mandi → (optional) Commodity
 * and shows a sortable price table with WhatsApp share per row.
 *
 * Props:
 *   lang {string}
 *   t    {object} – translations
 */
export default function RatesPage({ lang, t }) {
  const bf = getLangFont(lang);

  // ── Filter state ──
  const [state,     setState]     = useState("");
  const [district,  setDistrict]  = useState("");
  const [mandi,     setMandi]     = useState("");
  const [commodity, setCommodity] = useState("");

  // ── Data state ──
  const [prices,  setPrices]  = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Sort state ──
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Derived dropdown options
  const districts = state    ? Object.keys(STATES_DATA[state]?.districts || {}) : [];
  const mandis    = district  ? (STATES_DATA[state]?.districts[district] || [])  : [];

  // Fetch prices when mandi changes
  useEffect(() => {
    if (mandi) {
      setLoading(true);
      setTimeout(() => {
        setPrices(generatePrices(mandi));
        setLoading(false);
      }, 650);
    } else {
      setPrices([]);
    }
  }, [mandi]);

  // Filtered + sorted rows
  const filtered = prices
    .filter((p) => !commodity || p.key === commodity)
    .sort((a, b) => {
      const va = sortKey === "modalPrice" ? a.modalPrice : getName(a, lang).toLowerCase();
      const vb = sortKey === "modalPrice" ? b.modalPrice : getName(b, lang).toLowerCase();
      return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

  // Share a single commodity via WhatsApp
  const shareWA = (row) => {
    const msg =
      `🌾 ${getName(row, lang)}\n` +
      `📍 ${mandi}, ${district}, ${state}\n` +
      `📅 ${new Date().toLocaleDateString("en-IN")}\n\n` +
      `💰 Min: ₹${row.minPrice}\n` +
      `💰 Max: ₹${row.maxPrice}\n` +
      `💰 Modal: ₹${row.modalPrice}\n\n` +
      `📲 mandibhav.in`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const doSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "30px 16px 60px", fontFamily: bf }}>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize:   "clamp(22px,4vw,32px)",
            color:      "#1a4d2e",
            marginBottom: 3,
          }}
        >
          {t.heroTitle}
        </h1>
        <p style={{ color: "#9ca3af", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
          📅{" "}
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            year:    "numeric",
            month:   "long",
            day:     "numeric",
          })}
        </p>
      </div>

      {/* ── Filter panel ──────────────────────────────────── */}
      <div
        style={{
          background:   "#fff",
          borderRadius: 16,
          padding:      22,
          boxShadow:    "0 2px 16px rgba(0,0,0,0.07)",
          border:       "1px solid #e8e0d0",
          marginBottom: 22,
        }}
      >
        <div
          style={{
            display:             "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap:                 14,
          }}
        >
          {/* State */}
          <div>
            <label style={FILTER_LABEL_STYLE}>🗾 {t.state}</label>
            <select
              style={{ ...selectStyles(false), fontFamily: bf }}
              value={state}
              onChange={(e) => { setState(e.target.value); setDistrict(""); setMandi(""); setPrices([]); }}
            >
              <option value="">{t.selState}</option>
              {Object.keys(STATES_DATA).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          {/* Division / District */}
          <div>
            <label style={FILTER_LABEL_STYLE}>🗺️ {t.district}</label>
            <select
              style={{ ...selectStyles(!state), fontFamily: bf }}
              disabled={!state}
              value={district}
              onChange={(e) => { setDistrict(e.target.value); setMandi(""); setPrices([]); }}
            >
              <option value="">{t.selDist}</option>
              {districts.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>

          {/* Mandi */}
          <div>
            <label style={FILTER_LABEL_STYLE}>🏪 {t.mandi}</label>
            <select
              style={{ ...selectStyles(!district), fontFamily: bf }}
              disabled={!district}
              value={mandi}
              onChange={(e) => setMandi(e.target.value)}
            >
              <option value="">{t.selMandi}</option>
              {mandis.map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>

          {/* Commodity */}
          <div>
            <label style={FILTER_LABEL_STYLE}>🌾 {t.commodity}</label>
            <select
              style={{ ...selectStyles(false), fontFamily: bf }}
              value={commodity}
              onChange={(e) => setCommodity(e.target.value)}
            >
              <option value="">{t.selComm}</option>
              {COMMODITIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {getName(c, lang)}{lang !== "en" ? ` (${c.en})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Results table ────────────────────────────────── */}
      <div
        style={{
          background:   "#fff",
          borderRadius: 16,
          overflow:     "hidden",
          boxShadow:    "0 2px 16px rgba(0,0,0,0.07)",
          border:       "1px solid #e8e0d0",
        }}
      >
        {loading ? (
          /* Loading state */
          <div style={{ padding: "60px 24px", textAlign: "center", color: "#1a4d2e", fontFamily: bf }}>
            <div style={{ fontSize: 40, marginBottom: 14, display: "inline-block", animation: "spin 1.5s linear infinite" }}>
              🌾
            </div>
            <div>{t.loading}</div>
          </div>
        ) : filtered.length === 0 ? (
          /* Empty state */
          <div style={{ padding: "60px 24px", textAlign: "center", color: "#9ca3af", fontFamily: bf }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏪</div>
            <div style={{ fontSize: 15 }}>{t.noData}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {/* Table meta bar */}
            <div
              style={{
                padding:         "13px 20px",
                background:      "#f0f7f4",
                borderBottom:    "1px solid #d4ead8",
                display:         "flex",
                justifyContent:  "space-between",
                flexWrap:        "wrap",
                gap:             8,
              }}
            >
              <div style={{ fontWeight: 600, color: "#1a4d2e", fontSize: 13, fontFamily: bf }}>
                📍 {mandi} · {district} · {state}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
                {t.lastUp}: {new Date().toLocaleTimeString("en-IN")}
              </div>
            </div>

            {/* Table */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    ["name",       t.commodity],
                    ["minPrice",   t.minP],
                    ["maxPrice",   t.maxP],
                    ["modalPrice", t.modalP],
                    ["trend",      t.trend],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => doSort(key)}
                      style={{
                        padding:       "11px 15px",
                        textAlign:     "left",
                        fontWeight:    700,
                        fontSize:      11,
                        color:         "#1a4d2e",
                        background:    "#f0f7f4",
                        cursor:        "pointer",
                        userSelect:    "none",
                        borderBottom:  "2px solid #d4ead8",
                        whiteSpace:    "nowrap",
                        fontFamily:    "'DM Sans', sans-serif",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {label} {sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                  ))}
                  <th
                    style={{
                      padding:       "11px 15px",
                      background:    "#f0f7f4",
                      borderBottom:  "2px solid #d4ead8",
                      fontSize:      11,
                      fontFamily:    "'DM Sans', sans-serif",
                      color:         "#1a4d2e",
                      fontWeight:    700,
                      textTransform: "uppercase",
                    }}
                  >
                    📲
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((row, i) => (
                  <tr
                    key={row.key}
                    style={{ background: i % 2 === 0 ? "#fff" : "#fafaf8" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7f4")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafaf8")}
                  >
                    {/* Commodity name */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6" }}>
                      <div style={{ fontWeight: 600, color: "#1a1a1a", fontSize: 14, fontFamily: bf }}>
                        {getName(row, lang)}
                      </div>
                      {lang !== "en" && (
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
                          {row.en}
                        </div>
                      )}
                    </td>

                    {/* Min price */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6", fontFamily: "monospace", fontSize: 14, color: "#475569" }}>
                      ₹{row.minPrice.toLocaleString("en-IN")}
                    </td>

                    {/* Max price */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6", fontFamily: "monospace", fontSize: 14, color: "#475569" }}>
                      ₹{row.maxPrice.toLocaleString("en-IN")}
                    </td>

                    {/* Modal price */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6" }}>
                      <span
                        style={{
                          background:   "#e8f5e9",
                          color:        "#1a4d2e",
                          padding:      "3px 10px",
                          borderRadius: 20,
                          fontSize:     14,
                          fontWeight:   700,
                          fontFamily:   "monospace",
                        }}
                      >
                        ₹{row.modalPrice.toLocaleString("en-IN")}
                      </span>
                    </td>

                    {/* Trend */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6" }}>
                      <TrendBadge trend={row.trend} change={row.change} />
                    </td>

                    {/* WhatsApp share */}
                    <td style={{ padding: "12px 15px", borderBottom: "1px solid #f0ede6" }}>
                      <button
                        onClick={() => shareWA(row)}
                        style={{
                          background:   "#25D366",
                          color:        "#fff",
                          border:       "none",
                          borderRadius: 20,
                          padding:      "5px 12px",
                          fontSize:     12,
                          fontWeight:   600,
                          cursor:       "pointer",
                          fontFamily:   "'DM Sans', sans-serif",
                        }}
                      >
                        {t.share}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer note */}
            <div
              style={{
                padding:     "9px 18px",
                fontSize:    11,
                color:       "#9ca3af",
                borderTop:   "1px solid #f0ede6",
                fontFamily:  "'DM Sans', sans-serif",
              }}
            >
              * {t.per} · Source: AGMARKNET / data.gov.in
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
